import { useEffect, useState, useCallback } from "react";
import { Wallet, Plus, RefreshCw, Search, Pencil, Trash2, X, AlertTriangle, Link2, MessageCircle, Download, FileSpreadsheet, Printer, Mail } from "lucide-react";
import api from "../../lib/api";
import { errText } from "../../lib/errors";
import { toast } from "sonner";
import { downloadApi, printApiPdf } from "../../lib/download";

const tl = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 w-full";
const EMPTY = { name: "", type: "musteri", company: "", tax_office: "", tax_no: "11111111111", phone: "", email: "", address: "", note: "", sales_channel: "", account_code: "", belge_tipi: "", dukkan_bilgisi: "" };
const BELGE_OPTS = [{ v: "", l: "Otomatik (VKN/TCKN'e göre)" }, { v: "efatura", l: "e-Fatura Mükellefi" }, { v: "earsiv", l: "e-Arşiv Mükellefi" }];
const DUKKAN_OPTS = [{ v: "", l: "Seçiniz" }, { v: "sahis", l: "Şahıs (Dükkansız)" }, { v: "market", l: "Market" }, { v: "bakkal", l: "Bakkal / Şarküteri" }, { v: "restoran", l: "Restoran / Kafe" }, { v: "toptanci", l: "Toptancı" }, { v: "diger", l: "Diğer İşletme" }];
const TYPE_OPTS = [{ v: "musteri", l: "Müşteri" }, { v: "tedarikci", l: "Tedarikçi" }, { v: "diger", l: "Diğer" }];
const RISK_UI = { A: { label: "Güvenilir", cls: "bg-emerald-100 text-emerald-800" }, B: { label: "Takip", cls: "bg-amber-100 text-amber-800" }, C: { label: "Riskli", cls: "bg-red-100 text-red-700" } };
const CHANNEL_OPTS = [{ v: "dukkan", l: "Dükkan" }, { v: "depo", l: "Depo" }, { v: "web", l: "E-Ticaret Sitesi" }, { v: "b2b", l: "B2B" }, { v: "trendyol", l: "Trendyol" }, { v: "hepsiburada", l: "Hepsiburada" }, { v: "n11", l: "N11" }, { v: "pazarama", l: "Pazarama" }, { v: "ciceksepeti", l: "ÇiçekSepeti" }, { v: "pttavm", l: "PTT AVM" }, { v: "getir", l: "Getir" }, { v: "yemeksepeti", l: "Yemeksepeti" }, { v: "trendyolgo", l: "Trendyol Go" }, { v: "ubereats", l: "Uber Eats" }];

export default function AdminCari() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [txn, setTxn] = useState({ direction: "borc", amount: "", description: "", due_date: "", date: "" });
  const [share, setShare] = useState("");
  const [risk, setRisk] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (typeFilter) params.type = typeFilter;
      const [{ data: list }, { data: sum }, { data: riskMap }] = await Promise.all([
        api.get("/admin/cari", { params }),
        api.get("/admin/cari/summary"),
        api.get("/admin/cari-risk"),
      ]);
      setItems(list);
      setSummary(sum);
      setRisk(riskMap || {});
    } catch { toast.error("Cari listesi yüklenemedi"); }
  }, [q, typeFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openDetail = async (c) => {
    try {
      const { data } = await api.get(`/admin/cari/${c.cari_id}/statement`);
      setDetail(data);
      setTxn({ direction: "borc", amount: "", description: "", due_date: "" });
      setShare("");
    } catch { toast.error("Ekstre yüklenemedi"); }
  };

  const saveForm = async () => {
    if (!form.data.name.trim()) { toast.error("Ünvan/ad zorunlu"); return; }
    setBusy(true);
    try {
      if (form.mode === "create") await api.post("/admin/cari", form.data);
      else await api.put(`/admin/cari/${form.id}`, form.data);
      toast.success("Cari kaydedildi");
      setForm(null);
      load();
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const removeCari = async (id) => {
    if (!window.confirm("Cari silinsin mi? (Bakiyesi sıfır olmalı)")) return;
    try {
      await api.delete(`/admin/cari/${id}`);
      toast.success("Cari silindi");
      setDetail(null);
      load();
    } catch (e) { toast.error(errText(e) || "Silinemedi"); }
  };

  const ensureShare = async () => {
    if (share) return share;
    const { data } = await api.post(`/admin/cari/${detail.cari.cari_id}/share`);
    const url = `${window.location.origin}${data.share_path}`;
    setShare(url);
    return url;
  };

  const shareLink = async () => {
    try {
      const url = await ensureShare();
      await navigator.clipboard.writeText(url);
      toast.success("Ekstre linki kopyalandı");
    } catch { toast.error("Link oluşturulamadı"); }
  };

  const shareWhatsApp = async () => {
    try {
      const url = await ensureShare();
      const text = encodeURIComponent(`Sayın ${detail.cari.name}, güncel hesap ekstrenizi görüntüleyebilirsiniz: ${url}`);
      const digits = (detail.cari.phone || "").replace(/\D/g, "");
      const waPhone = digits.startsWith("0") ? `9${digits}` : digits;
      window.open(`https://wa.me/${waPhone}?text=${text}`, "_blank");
    } catch { toast.error("Link oluşturulamadı"); }
  };

  const downloadPdf = () => downloadApi(`/admin/cari/${detail.cari.cari_id}/statement.pdf`, "ekstre.pdf").catch((e) => toast.error(e.message || "PDF oluşturulamadı"));

  const downloadExcel = () => downloadApi(`/admin/reports/cari-ekstre.xlsx?cari_id=${detail.cari.cari_id}`, "cari-ekstre.xlsx").catch((e) => toast.error(e.message || "Excel oluşturulamadı"));

  const printPdf = () => printApiPdf(`/admin/cari/${detail.cari.cari_id}/statement.pdf`).catch((e) => toast.error(e.message || "Yazdırılamadı"));

  const submitTxn = async () => {
    const amount = parseFloat(txn.amount);
    if (!amount || amount <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/cari/${detail.cari.cari_id}/transaction`, { ...txn, amount });
      toast.success("İşlem eklendi");
      openDetail(detail.cari);
      load();
    } catch (e) { toast.error(errText(e) || "İşlem eklenemedi"); }
    finally { setBusy(false); }
  };

  const syncOrders = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cari/sync-orders");
      toast.success(`${data.created} cari oluşturuldu, ${data.skipped} kayıt atlandı`);
      load();
    } catch { toast.error("Senkronizasyon başarısız"); }
    finally { setBusy(false); }
  };

  const setF = (k, v) => setForm((f) => ({ ...f, data: { ...f.data, [k]: v } }));

  return (
    <div data-testid="admin-cari-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Cari Hesaplar</h1>
          <p className="text-stone-500 text-sm mt-1">Müşteri ve tedarikçi borç/alacak takibi, ekstre ve vade yönetimi.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="cari-sync-button" onClick={syncOrders} disabled={busy} className="flex items-center gap-2 border border-forest text-forest hover:bg-forest/5 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
            <RefreshCw className="w-4 h-4" /> Müşterileri Senkronize Et
          </button>
          <button data-testid="cari-new-button" onClick={() => setForm({ mode: "create", data: { ...EMPTY } })} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
            <Plus className="w-4 h-4" /> Yeni Cari
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div data-testid="cari-summary-receivable" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
            <p className="text-xs text-stone-500 uppercase tracking-wide">Toplam Alacak</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{tl(summary.total_receivable)} ₺</p>
          </div>
          <div data-testid="cari-summary-payable" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
            <p className="text-xs text-stone-500 uppercase tracking-wide">Toplam Borç</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{tl(summary.total_payable)} ₺</p>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
            <p className="text-xs text-stone-500 uppercase tracking-wide">Cari Sayısı</p>
            <p className="text-2xl font-bold text-stone-900 mt-1">{summary.cari_count}</p>
          </div>
        </div>
      )}

      {summary?.upcoming_dues?.length > 0 && (
        <div data-testid="cari-upcoming-dues" className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="w-4 h-4" /> Vadesi Yaklaşan / Geçen İşlemler</p>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            {summary.upcoming_dues.map((d) => (
              <li key={d.txn_id}>{d.due_date} — {d.cari_name} — {d.direction === "borc" ? "Tahsil edilecek" : "Ödenecek"}: <b>{tl(d.amount)} ₺</b> {d.description && `(${d.description})`}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid="cari-search-input" className={`${inputCls} pl-10`} placeholder="Ünvan, e-posta, telefon ara..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="cari-type-filter" className={`${inputCls} w-auto`} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">Tüm Türler</option>
          {TYPE_OPTS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300">
            <th className="px-5 py-3">Ünvan / Ad</th><th className="px-5 py-3">Tür</th><th className="px-5 py-3">Telefon</th><th className="px-5 py-3">E-posta</th><th className="px-5 py-3 text-right">Bakiye</th><th className="px-5 py-3 text-center">Risk</th>
          </tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.cari_id} data-testid={`cari-row-${c.cari_id}`} onClick={() => openDetail(c)} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors">
                <td className="px-5 py-3 font-medium text-stone-900">{c.name}{c.account_code && <span className="block text-[10px] font-mono font-bold text-gold-dark">{c.account_code}</span>}{c.company && <span className="block text-xs text-stone-400 font-normal">{c.company}</span>}</td>
                <td className="px-5 py-3"><span className="px-2 py-1 rounded-lg bg-stone-100 text-xs font-medium">{c.type_label}</span>{c.channel_label && <span className="block text-[10px] text-stone-500 mt-1">{c.channel_label}</span>}</td>
                <td className="px-5 py-3 text-stone-600">{c.phone || "-"}</td>
                <td className="px-5 py-3 text-stone-600">{c.email || "-"}</td>
                <td className={`px-5 py-3 text-right font-semibold ${c.balance > 0 ? "text-emerald-700" : c.balance < 0 ? "text-red-600" : "text-stone-500"}`}>{tl(c.balance)} ₺</td>
                <td className="px-5 py-3 text-center">{risk[c.cari_id] ? <span data-testid={`cari-risk-${c.cari_id}`} title={`Vadesi geçmiş: ${tl(risk[c.cari_id].overdue)} ₺ · Önerilen limit: ${tl(risk[c.cari_id].suggested_limit)} ₺`} className={`text-[10px] font-bold px-2 py-1 rounded-full ${RISK_UI[risk[c.cari_id].grade].cls}`}>{risk[c.cari_id].grade} {RISK_UI[risk[c.cari_id].grade].label}</span> : <span className="text-stone-300 text-xs">—</span>}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-stone-400"><Wallet className="w-8 h-8 mx-auto mb-2 opacity-40" />Cari kaydı bulunamadı</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <div data-testid="cari-form-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">{form.mode === "create" ? "Yeni Cari" : "Cari Düzenle"}</h3>
              <button data-testid="cari-form-close" onClick={() => setForm(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* SOL: kimlik & iletişim */}
              <div className="space-y-3 lg:pr-6 lg:border-r lg:border-stone-200">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Kimlik & İletişim</p>
                <div><label className="text-xs font-semibold text-stone-600">Ünvan / Ad Soyad *</label><input data-testid="cari-form-name" className={inputCls} value={form.data.name} onChange={(e) => setF("name", e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-semibold text-stone-600">Tür</label>
                    <select data-testid="cari-form-type" className={inputCls} value={form.data.type} onChange={(e) => setF("type", e.target.value)}>{TYPE_OPTS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}</select></div>
                  <div><label className="text-xs font-semibold text-stone-600">Satış Kanalı</label>
                    <select data-testid="cari-form-channel" className={inputCls} value={form.data.sales_channel} onChange={(e) => setF("sales_channel", e.target.value)}>
                      <option value="">Seçilmedi</option>
                      {CHANNEL_OPTS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                    </select></div>
                </div>
                <div><label className="text-xs font-semibold text-stone-600">Firma</label><input className={inputCls} value={form.data.company} onChange={(e) => setF("company", e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-semibold text-stone-600">Telefon</label><input className={inputCls} value={form.data.phone} onChange={(e) => setF("phone", e.target.value)} /></div>
                  <div><label className="text-xs font-semibold text-stone-600">E-posta</label><input className={inputCls} value={form.data.email} onChange={(e) => setF("email", e.target.value)} /></div>
                </div>
                <div><label className="text-xs font-semibold text-stone-600">Adres</label><input className={inputCls} value={form.data.address} onChange={(e) => setF("address", e.target.value)} /></div>
              </div>
              {/* SAĞ: mali bilgiler */}
              <div className="space-y-3">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Mali Bilgiler</p>
                <div><label className="text-xs font-semibold text-stone-600">Hesap Kodu <span className="text-stone-400 normal-case font-normal">(boş = otomatik)</span></label><input data-testid="cari-form-account-code" className={inputCls} value={form.data.account_code} onChange={(e) => setF("account_code", e.target.value)} placeholder={form.data.type === "tedarikci" ? "320.001" : "120.001"} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-semibold text-stone-600">Vergi Dairesi</label><input className={inputCls} value={form.data.tax_office} onChange={(e) => setF("tax_office", e.target.value)} /></div>
                  <div><label className="text-xs font-semibold text-stone-600">Vergi / TC No</label><input className={inputCls} value={form.data.tax_no} onChange={(e) => setF("tax_no", e.target.value)} /></div>
                </div>
                <div><label className="text-xs font-semibold text-stone-600">E-Belge Mükellefiyeti</label>
                  <select data-testid="cari-form-belge-tipi" className={inputCls} value={form.data.belge_tipi || ""} onChange={(e) => setF("belge_tipi", e.target.value)}>
                    {BELGE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                  <p className="text-[10px] text-stone-400 mt-1">Fatura entegratöre gönderilirken belge türü buradan tespit edilir.</p></div>
                <div><label className="text-xs font-semibold text-stone-600">Dükkan Bilgileri</label>
                  <select data-testid="cari-form-dukkan" className={inputCls} value={form.data.dukkan_bilgisi || ""} onChange={(e) => setF("dukkan_bilgisi", e.target.value)}>
                    {DUKKAN_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select></div>
                <div><label className="text-xs font-semibold text-stone-600">Not</label><input className={inputCls} value={form.data.note} onChange={(e) => setF("note", e.target.value)} /></div>
              </div>
            </div>
            <button data-testid="cari-form-submit" onClick={saveForm} disabled={busy} className="mt-5 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </div>
      )}

      {detail && (
        <div data-testid="cari-detail-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-serif text-xl text-forest">{detail.cari.name}</h3>
                <p className="text-xs text-stone-500">{detail.cari.account_code && <span className="font-mono font-bold text-gold-dark">{detail.cari.account_code}</span>} {detail.cari.type_label} {detail.cari.channel_label && `• ${detail.cari.channel_label}`} {detail.cari.email && `• ${detail.cari.email}`} {detail.cari.phone && `• ${detail.cari.phone}`}</p>
                <p className={`text-2xl font-bold mt-2 ${detail.balance > 0 ? "text-emerald-700" : detail.balance < 0 ? "text-red-600" : "text-stone-500"}`} data-testid="cari-detail-balance">{tl(detail.balance)} ₺ <span className="text-xs font-normal text-stone-400">{detail.balance > 0 ? "(borçlu)" : detail.balance < 0 ? "(alacaklı)" : ""}</span></p>
                {risk[detail.cari.cari_id] && (
                  <div data-testid="cari-risk-panel" className="mt-3 bg-stone-50 border border-stone-200 rounded-xl p-3 text-xs space-y-1">
                    <p className="font-bold text-stone-700 flex items-center gap-2 flex-wrap">Akıllı Cari Analizi (son 90 gün)
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${RISK_UI[risk[detail.cari.cari_id].grade].cls}`}>{risk[detail.cari.cari_id].grade} — {RISK_UI[risk[detail.cari.cari_id].grade].label}</span></p>
                    <p className="text-stone-500">Vadesi geçmiş tutar: <b className={risk[detail.cari.cari_id].overdue > 0 ? "text-red-600" : "text-emerald-700"}>{tl(risk[detail.cari.cari_id].overdue)} ₺</b> · 90 günlük işlem hacmi: {tl(risk[detail.cari.cari_id].turnover_90d)} ₺</p>
                    <p className="text-stone-500">Önerilen kredi limiti: <b className="text-forest">{tl(risk[detail.cari.cari_id].suggested_limit)} ₺</b></p>
                  </div>
                )}
              </div>
              <div className="flex gap-1.5">
                <button data-testid="cari-edit-button" onClick={() => { setForm({ mode: "edit", id: detail.cari.cari_id, data: { ...EMPTY, ...detail.cari } }); }} className="p-2 hover:bg-stone-100 rounded-lg"><Pencil className="w-4 h-4 text-stone-600" /></button>
                <button data-testid="cari-delete-button" onClick={() => removeCari(detail.cari.cari_id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                <button data-testid="cari-close-detail" onClick={() => setDetail(null)} className="p-2 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <button data-testid="cari-share-link" onClick={shareLink} className="flex items-center gap-1.5 text-xs font-semibold border border-forest text-forest hover:bg-forest/5 px-3.5 py-2 rounded-xl transition-colors"><Link2 className="w-3.5 h-3.5" /> {share ? "Link Kopyala (hazır)" : "Ekstre Linki Oluştur"}</button>
              <button data-testid="cari-share-whatsapp" onClick={shareWhatsApp} className="flex items-center gap-1.5 text-xs font-semibold bg-[#25D366] hover:bg-[#1fb857] text-white px-3.5 py-2 rounded-xl transition-colors"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp ile Gönder</button>
              <button data-testid="cari-statement-pdf" onClick={downloadPdf} className="flex items-center gap-1.5 text-xs font-semibold bg-stone-800 hover:bg-stone-900 text-white px-3.5 py-2 rounded-xl transition-colors"><Download className="w-3.5 h-3.5" /> PDF İndir</button>
              <button data-testid="cari-statement-excel" onClick={downloadExcel} className="flex items-center gap-1.5 text-xs font-semibold border border-forest text-forest hover:bg-forest/5 px-3.5 py-2 rounded-xl transition-colors"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
              <button data-testid="cari-statement-print" onClick={printPdf} className="flex items-center gap-1.5 text-xs font-semibold border border-stone-300 text-stone-600 hover:bg-stone-50 px-3.5 py-2 rounded-xl transition-colors"><Printer className="w-3.5 h-3.5" /> Yazdır</button>
              <a data-testid="cari-statement-mail" href={`mailto:${detail.cari.email || ""}?subject=${encodeURIComponent("Hesap Ekstreniz — Ata Angora")}&body=${encodeURIComponent("Ekstre linkiniz: " + (share || "(önce 'Ekstre Linki Oluştur' butonuna basın)"))}`} className="flex items-center gap-1.5 text-xs font-semibold border border-stone-300 text-stone-600 hover:bg-stone-50 px-3.5 py-2 rounded-xl transition-colors"><Mail className="w-3.5 h-3.5" /> Mail</a>
            </div>

            <div className="bg-stone-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
              <div><label className="text-xs font-semibold text-stone-600">Yön</label>
                <select data-testid="cari-txn-direction" className={inputCls} value={txn.direction} onChange={(e) => setTxn({ ...txn, direction: e.target.value })}>
                  <option value="borc">Borç (tahsil edilecek)</option>
                  <option value="alacak">Alacak (ödeme/tahsilat)</option>
                </select></div>
              <div><label className="text-xs font-semibold text-stone-600">Tutar ₺</label><input data-testid="cari-txn-amount" type="number" min="0" step="0.01" className={inputCls} value={txn.amount} onChange={(e) => setTxn({ ...txn, amount: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">İşlem Tarihi</label><input data-testid="cari-txn-date" type="date" className={inputCls} value={txn.date} onChange={(e) => setTxn({ ...txn, date: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Vade</label><input data-testid="cari-txn-due" type="date" className={inputCls} value={txn.due_date} onChange={(e) => setTxn({ ...txn, due_date: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Açıklama</label><input data-testid="cari-txn-desc" className={inputCls} value={txn.description} onChange={(e) => setTxn({ ...txn, description: e.target.value })} /></div>
              <button data-testid="cari-txn-submit" onClick={submitTxn} disabled={busy} className="h-11 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold rounded-xl transition-colors">Ekle</button>
            </div>

            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300">
                  <th className="py-2 pr-3">Tarih</th><th className="py-2 pr-3">Açıklama</th><th className="py-2 pr-3">Vade</th><th className="py-2 pr-3 text-right">Borç</th><th className="py-2 pr-3 text-right">Alacak</th><th className="py-2 text-right">Bakiye</th>
                </tr></thead>
                <tbody data-testid="cari-statement-list">
                  {detail.transactions.map((t) => (
                    <tr key={t.txn_id} className="border-b border-stone-100">
                      <td className="py-2 pr-3 text-stone-600">{(t.date || t.created_at || "").slice(0, 10)}</td>
                      <td className="py-2 pr-3">{t.description || "-"}</td>
                      <td className="py-2 pr-3 text-stone-600">{t.due_date || "-"}</td>
                      <td className="py-2 pr-3 text-right text-emerald-700">{t.direction === "borc" ? `${tl(t.amount)} ₺` : ""}</td>
                      <td className="py-2 pr-3 text-right text-red-600">{t.direction === "alacak" ? `${tl(t.amount)} ₺` : ""}</td>
                      <td className="py-2 text-right font-medium">{tl(t.running)} ₺</td>
                    </tr>
                  ))}
                  {detail.transactions.length === 0 && <tr><td colSpan="6" className="py-6 text-center text-stone-400">Henüz işlem yok</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
